import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { questions } from "@/mocks/questions";
import { recommendations, videos } from "@/mocks/recommendations";

export default function Admin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");

  const mockSales = [
    { id: 1, user: "maria@email.com", amount: 9.90, date: "2026-06-20", status: "approved" },
    { id: 2, user: "joao@email.com", amount: 9.90, date: "2026-06-19", status: "approved" },
    { id: 3, user: "ana@email.com", amount: 9.90, date: "2026-06-18", status: "pending" },
    { id: 4, user: "carlos@email.com", amount: 9.90, date: "2026-06-17", status: "approved" },
    { id: 5, user: "fernanda@email.com", amount: 9.90, date: "2026-06-15", status: "approved" },
  ];

  const mockLeads = [
    { id: 1, email: "teste1@email.com", date: "2026-06-20", completed: true, paid: true },
    { id: 2, email: "teste2@email.com", date: "2026-06-19", completed: true, paid: false },
    { id: 3, email: "teste3@email.com", date: "2026-06-18", completed: false, paid: false },
    { id: 4, email: "teste4@email.com", date: "2026-06-17", completed: true, paid: true },
    { id: 5, email: "teste5@email.com", date: "2026-06-16", completed: true, paid: false },
  ];

  const totalRevenue = mockSales.reduce((acc, s) => s.status === "approved" ? acc + s.amount : acc, 0);
  const totalTests = mockLeads.length;
  const totalPaid = mockLeads.filter(l => l.paid).length;
  const conversionRate = ((totalPaid / totalTests) * 100).toFixed(1);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Header */}
      <div className="bg-primary-500 py-6 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl text-background-50">Área Administrativa</h1>
            <p className="font-body text-sm text-background-200/70">Prévia do painel — dados fictícios, sem gestão real</p>
          </div>
          <button
            onClick={() => navigate("/")}
            className="font-body text-sm text-background-200/70 hover:text-background-50 transition-colors cursor-pointer"
          >
            Sair do painel
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line" },
            { id: "questions", label: "Perguntas", icon: "ri-questionnaire-line" },
            { id: "videos", label: "Vídeos", icon: "ri-video-line" },
            { id: "recommendations", label: "Recomendações", icon: "ri-file-list-line" },
            { id: "sales", label: "Vendas", icon: "ri-money-cny-circle-line" },
            { id: "leads", label: "Leads", icon: "ri-user-line" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-body text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                activeTab === tab.id
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-secondary-500 hover:text-foreground-950"
              }`}
            >
              <i className={tab.icon}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard */}
        {activeTab === "dashboard" && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
              <p className="font-body text-sm text-secondary-500 mb-1">Receita Total</p>
              <p className="font-heading text-3xl text-primary-500">R$ {totalRevenue.toFixed(2)}</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
              <p className="font-body text-sm text-secondary-500 mb-1">Testes Realizados</p>
              <p className="font-heading text-3xl text-accent-500">{totalTests}</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
              <p className="font-body text-sm text-secondary-500 mb-1">Pagamentos</p>
              <p className="font-heading text-3xl text-primary-500">{totalPaid}</p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 border border-primary-500/5">
              <p className="font-body text-sm text-secondary-500 mb-1">Taxa de Conversão</p>
              <p className="font-heading text-3xl text-secondary-500">{conversionRate}%</p>
            </div>
          </div>
        )}

        {/* Questions */}
        {activeTab === "questions" && (
          <div className="bg-background-100 rounded-2xl border border-primary-500/5 overflow-hidden">
            <div className="p-6 border-b border-primary-500/5 flex items-center justify-between">
              <h3 className="font-heading text-foreground-950 text-lg">Gerenciar Perguntas</h3>
              <span className="font-body text-sm text-secondary-500">{questions.length} perguntas</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-50">
                  <tr>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">#</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Pergunta</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Dosha</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((q) => (
                    <tr key={q.id} className="border-t border-primary-500/5 hover:bg-background-50/50">
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">{q.id}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">{q.text}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full font-body text-xs ${
                          q.dosha === "vata" ? "bg-accent-100 text-accent-700" :
                          q.dosha === "pitta" ? "bg-primary-100 text-primary-700" :
                          "bg-secondary-100 text-secondary-700"
                        }`}>
                          {q.dosha}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button className="w-8 h-8 rounded-lg bg-primary-50 text-primary-500 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Videos */}
        {activeTab === "videos" && (
          <div className="bg-background-100 rounded-2xl border border-primary-500/5 overflow-hidden">
            <div className="p-6 border-b border-primary-500/5 flex items-center justify-between">
              <h3 className="font-heading text-foreground-950 text-lg">Gerenciar Vídeos</h3>
              <span className="font-body text-sm text-secondary-500">{videos.length} vídeos</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-50">
                  <tr>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Título</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Dosha</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Categoria</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {videos.map((v) => (
                    <tr key={v.id} className="border-t border-primary-500/5 hover:bg-background-50/50">
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">{v.title}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full font-body text-xs ${
                          v.dosha === "vata" ? "bg-accent-100 text-accent-700" :
                          v.dosha === "pitta" ? "bg-primary-100 text-primary-700" :
                          "bg-secondary-100 text-secondary-700"
                        }`}>
                          {v.dosha}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-body text-sm text-secondary-500">{v.category}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button className="w-8 h-8 rounded-lg bg-primary-50 text-primary-500 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recommendations */}
        {activeTab === "recommendations" && (
          <div className="bg-background-100 rounded-2xl border border-primary-500/5 overflow-hidden">
            <div className="p-6 border-b border-primary-500/5 flex items-center justify-between">
              <h3 className="font-heading text-foreground-950 text-lg">Gerenciar Recomendações</h3>
              <span className="font-body text-sm text-secondary-500">{recommendations.length} recomendações</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-50">
                  <tr>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Título</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Dosha</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Categoria</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map((r) => (
                    <tr key={r.id} className="border-t border-primary-500/5 hover:bg-background-50/50">
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">{r.title}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full font-body text-xs ${
                          r.dosha === "vata" ? "bg-accent-100 text-accent-700" :
                          r.dosha === "pitta" ? "bg-primary-100 text-primary-700" :
                          "bg-secondary-100 text-secondary-700"
                        }`}>
                          {r.dosha}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-body text-sm text-secondary-500">{r.category}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button className="w-8 h-8 rounded-lg bg-primary-50 text-primary-500 flex items-center justify-center hover:bg-primary-100 transition-colors cursor-pointer">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button className="w-8 h-8 rounded-lg bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 transition-colors cursor-pointer">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sales */}
        {activeTab === "sales" && (
          <div className="bg-background-100 rounded-2xl border border-primary-500/5 overflow-hidden">
            <div className="p-6 border-b border-primary-500/5">
              <h3 className="font-heading text-foreground-950 text-lg">Vendas</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-50">
                  <tr>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">ID</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Usuário</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Valor</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Data</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {mockSales.map((s) => (
                    <tr key={s.id} className="border-t border-primary-500/5 hover:bg-background-50/50">
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">#{s.id}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">{s.user}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">R$ {s.amount.toFixed(2)}</td>
                      <td className="px-6 py-4 font-body text-sm text-secondary-500">{s.date}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full font-body text-xs ${
                          s.status === "approved" ? "bg-primary-100 text-primary-700" : "bg-accent-100 text-accent-700"
                        }`}>
                          {s.status === "approved" ? "Aprovado" : "Pendente"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Leads */}
        {activeTab === "leads" && (
          <div className="bg-background-100 rounded-2xl border border-primary-500/5 overflow-hidden">
            <div className="p-6 border-b border-primary-500/5">
              <h3 className="font-heading text-foreground-950 text-lg">Leads</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background-50">
                  <tr>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">ID</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Email</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Data</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Completou Teste</th>
                    <th className="text-left font-body text-sm font-medium text-secondary-500 px-6 py-3">Pagou</th>
                  </tr>
                </thead>
                <tbody>
                  {mockLeads.map((l) => (
                    <tr key={l.id} className="border-t border-primary-500/5 hover:bg-background-50/50">
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">#{l.id}</td>
                      <td className="px-6 py-4 font-body text-sm text-foreground-950">{l.email}</td>
                      <td className="px-6 py-4 font-body text-sm text-secondary-500">{l.date}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full font-body text-xs ${
                          l.completed ? "bg-primary-100 text-primary-700" : "bg-secondary-100 text-secondary-700"
                        }`}>
                          {l.completed ? "Sim" : "Não"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full font-body text-xs ${
                          l.paid ? "bg-accent-100 text-accent-700" : "bg-secondary-100 text-secondary-700"
                        }`}>
                          {l.paid ? "Sim" : "Não"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
